import React, { useEffect, useState } from "react"
import { useQuery } from "@apollo/client"
import { useSearchParams } from "react-router-dom"
import { Badge, Card, CardBody, Col, Collapse, Container, Row } from "reactstrap"
import Loader from "../../../../../Material/Loader"
import { AllTasksQuery } from "../../../../../TaskManagement/Queries"
import TaskDetailsSidePanel from "../../../../../TaskManagement/TaskDetailsSidePanel"
import TaskList from "../../../../../TaskManagement/TaskList"
import TaskManagementListHeader from "../../../../../TaskManagement/TaskManagementListHeader"
import NewTaskModal from "../../../../../TaskManagement/TaskComponents/NewTaskModal"

const formatTaskStatus = (status) => (status || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())

const formatTaskDate = (dateValue) => {
    if (!dateValue) return "--"

    const date = new Date(dateValue)
    if (Number.isNaN(date.getTime())) return "--"

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date)
}

const getStatusColor = (status) => {
    const normalizedStatus = (status || "").replace(/ /g, "_")
    if (normalizedStatus === "DONE") return "success"
    if (normalizedStatus === "CANCELED") return "secondary"
    if (normalizedStatus === "IN_PROGRESS") return "warning"
    if (normalizedStatus === "INCOMPLETE") return "danger"
    return "primary"
}

const getAssigneeText = (task) => {
    const assignees = task?.assignedTo?.edges?.map(({ node }) => {
        const fullName = [node.firstName, node.lastName].filter(Boolean).join(" ")
        return fullName || node.username
    }).filter(Boolean) || []

    return assignees.length > 0 ? assignees.join(", ") : "Unassigned"
}

const DriverTasksTab = ({ driverId, isSidebar = false }) => {
    const [orderBy] = useState(['-date_added'])
    const [filterValues, setFilterValues] = useState({ searchTerm: '', driverId: driverId, isArchived: false })

    useEffect(() => {
        setFilterValues((prevFilterValues) => ({ ...prevFilterValues, driverId }))
    }, [driverId])

    if (isSidebar) {
        return <DriverTasksSidebarList driverId={driverId} orderBy={orderBy} />
    }

    return (
        <Container fluid className={`driver-tasks-tab ${isSidebar ? "driver-tasks-sidebar" : ""}`}>
            <div className="info-container mt-0">
                <TaskManagementListHeader
                    activeTab="all"
                    filterValues={filterValues}
                    setFilterValues={(res) => setFilterValues({ ...res, driverId: driverId })}
                />
                <Row>
                    <Col className="TaskListColumn">
                        <TaskList {...filterValues} orderBy={orderBy} />
                    </Col>
                </Row>
            </div>
        </Container>
    )
}

const DriverTasksSidebarList = ({ driverId, orderBy }) => {
    const [openTaskId, setOpenTaskId] = useState("")
    const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const selectedTaskId = searchParams.get("task_id")
    const { data, loading } = useQuery(AllTasksQuery, {
        variables: {
            driverId,
            isArchived: false,
            orderBy,
            first: 20,
        },
        skip: !driverId,
        fetchPolicy: "cache-and-network",
    })

    const tasks = data?.allTasks?.edges?.map(({ node }) => node).filter(Boolean) || []

    const openTaskDetails = (taskId) => {
        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.set("task_id", taskId)
        setSearchParams(nextSearchParams)
    }

    if (loading && tasks.length === 0) return <Loader />

    return (
        <div className="driver-tasks-sidebar">
            {selectedTaskId && <TaskDetailsSidePanel />}
            {isNewTaskModalOpen && (
                <NewTaskModal
                    isOpen={isNewTaskModalOpen}
                    closeModal={() => setIsNewTaskModalOpen(false)}
                    filterValues={{ driverId }}
                />
            )}
            <div className="driver-tasks-sidebar-toolbar">
                <button
                    type="button"
                    className="driver-task-create-button"
                    onClick={() => setIsNewTaskModalOpen(true)}
                >
                    <i className="fa fa-plus-square" aria-hidden="true" />
                    New Task
                </button>
            </div>
            {tasks.length === 0 ? (
                <p>No tasks found for this driver.</p>
            ) : (
                <ul className="collapse-ul driver-task-list">
                    {tasks.map((task) => (
                        <li key={task.id} className="collapse-li">
                            <button
                                className={`collapse-button ${openTaskId === task.id ? "open" : ""}`}
                                type="button"
                                onClick={() => setOpenTaskId(openTaskId === task.id ? "" : task.id)}
                            >
                                <span className="collapse-button-label driver-task-button-label">
                                    <i className="fa fa-tasks collapse-leading-icon" aria-hidden="true" />
                                    <span className="driver-task-summary">
                                        <span className="driver-task-title">{task.title || "Untitled task"}</span>
                                        <span className="driver-task-list-meta">
                                            <span>
                                                <i className="fa fa-calendar-o" aria-hidden="true" />
                                                {formatTaskDate(task.dateAdded)}
                                            </span>
                                            <span>
                                                <i className="fa fa-comments-o" aria-hidden="true" />
                                                {task.commentsCount || 0} updates
                                            </span>
                                        </span>
                                    </span>
                                </span>
                                <span className="collapse-button-meta">
                                    <Badge color={getStatusColor(task.status)} className="driver-task-status">
                                        {formatTaskStatus(task.status)}
                                    </Badge>
                                    <i
                                        className={`collapse-arrow fa ${openTaskId === task.id ? "fa-chevron-up" : "fa-chevron-down"}`}
                                        aria-hidden="true"
                                    />
                                </span>
                            </button>
                            <Collapse isOpen={openTaskId === task.id}>
                                <Card className="driver-context-card driver-task-card">
                                    <CardBody>
                                        <dl className="context-info-list">
                                            <div className="context-info-row">
                                                <dt>Description</dt>
                                                <dd className="driver-task-description">{task.description || "--"}</dd>
                                            </div>
                                            <div className="context-info-row">
                                                <dt>Due Date</dt>
                                                <dd>{task.dueDate || "--"}</dd>
                                            </div>
                                            <div className="context-info-row">
                                                <dt>Assigned To</dt>
                                                <dd>{getAssigneeText(task)}</dd>
                                            </div>
                                            <div className="context-info-row">
                                                <dt>Board</dt>
                                                <dd>{task.board?.title || "--"}</dd>
                                            </div>
                                        </dl>
                                        <button
                                            type="button"
                                            className="driver-task-detail-button"
                                            onClick={() => openTaskDetails(task.id)}
                                        >
                                            Open Task
                                        </button>
                                    </CardBody>
                                </Card>
                            </Collapse>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default DriverTasksTab
